<template>
  <!-- Wrapper for the entire component -->
  <div
    ref="dropdownWrapper"
    v-O360-click-outside="handleClickOutside"
    class="dropdown-wrapper relative w-full"
    :data-dropdown-id="computedDropdownId"
  >
    <!-- Main dropdown container with border -->
    <div
      class="rounded border border-solid border-accent-200 bg-white hover:cursor-pointer"
      :class="[borderClass, zIndexCustom ? `z-[${zIndexCustom}]` : '']"
    >
      <!-- Dropdown toggle with caret and input field -->
      <div
        class="dropdown-toggle w-full flex justify-between items-center py-2 px-3"
        @click.stop="toggleDropdown"
      >
        <!-- Conditionally render input box or selected item name -->
        <input
          v-if="enableInput || !selected"
          ref="inputBox"
          v-model="inputValue"
          class="select-input w-full border-0 outline-none px-2 py-1 rounded bg-transparent min-h-7"
          type="text"
          :placeholder="placeholder"
          @keyup.enter="handleAdd"
        />
        <span v-else class="w-full px-2 py-1 min-h-7">
          {{ selected?.name }}
        </span>
        <!-- Caret icon toggling based on input state -->
        <span
          class="w-5 h-5 flex content-center items-center caret-class"
          :class="[caretClass]"
        >
          <font-awesome-icon v-if="enableInput" :icon="['far', 'chevron-up']" />
          <font-awesome-icon v-else :icon="['far', 'chevron-down']" />
        </span>
      </div>

      <!-- Dropdown options list -->
      <div
        v-if="enableInput"
        class="absolute w-full rounded border border-solid border-zinc-300 bg-white shadow-multisearch mt-2 z-50 p-2"
        :class="
          scrollable
            ? 'max-h-[200px] overflow-auto'
            : `h-[${options.length * 3.5}rem]`
        "
      >
        <div v-if="optionList.length === 0" class="flex p-4 text-zinc-600">
          {{ noOptionText }}
        </div>
        <div
          v-for="(item, index) in optionList"
          :key="item.id"
          :class="[
            'group',
            item.customStyleClass,
            {
              'font-semibold': !item.customStyleClass && !item.isNestedOption,
              'pl-13': item.isNestedOption,
              'rounded-t-lg': index === 0,
              'rounded-b-lg': index === optionList.length - 1,
              'cursor-not-allowed': isNestedOptionsParent(item),
              'bg-zinc-50': greyoutParentOptions && isNestedOptionsParent(item),
              'text-zinc-500':
                greyoutParentOptions && isNestedOptionsParent(item),
              'hover:bg-accent-50': !isNestedOptionsParent(item),
              'text-base': !isNestedOptionsParent(item),
              'text-sm': item.isNestedOption,
            },
          ]"
          class="flex p-2 h-9"
          @click.stop="
            isNestedOptionsParent(item)
              ? $event.stopPropagation()
              : optionSelected(item)
          "
        >
          <O360Tooltip
            :disabled="
              !nonSelectableOptionTooltip || !isNestedOptionsParent(item)
            "
            :text="nonSelectableOptionTooltip"
            :direction="nonSelectableOptionTooltipDirection"
          >
            <span v-if="item.icon" class="pr-2">
              <font-awesome-icon
                class="text-lg w-7 text-zinc-600"
                :class="[
                  {
                    'text-zinc-500': isNestedOptionsParent(item),
                  },
                ]"
                :icon="[item.iconType, item.icon]"
              />
            </span>
            <span class="font-body-2 group-hover:font-body-2-stronger">{{
              item.name
            }}</span>
          </O360Tooltip>
        </div>
      </div>
    </div>

    <!-- Error text displayed outside the main border -->
    <div class="w-full text-red-300 mt-1 pl-2">{{ errorText }}</div>
  </div>
</template>

<script setup lang="ts">
import {
  type Ref,
  ref,
  computed,
  type PropType,
  nextTick,
  onMounted,
  onUnmounted,
} from "vue";
import {
  O360Tooltip,
  TOOLTIP_DIRECTION,
  type ListItem,
} from "@airpr/360-ui-components";

// Generate a unique ID for each dropdown instance
const dropdownId = `dropdown-${Math.random().toString(36).substr(2, 9)}`;

const inputValue = ref<string>("");
const errorText = ref<string>("");
const enableInput = ref(false);
const inputBox: Ref<HTMLInputElement | null> = ref(null);
const dropdownWrapper: Ref<HTMLDivElement | null> = ref(null);

const emit = defineEmits(["add-custom", "optionSelected"]);

const props = defineProps({
  allowAdd: {
    type: Boolean,
    default: false,
  },
  options: {
    type: Array as PropType<ListItem[]>,
    required: true,
  },
  placeholder: {
    type: String,
    default: "",
  },
  zIndexCustom: {
    type: Number,
    default: 0,
    required: false,
  },
  selected: {
    type: Object as PropType<ListItem | undefined>,
    default: undefined,
  },
  scrollable: {
    type: Boolean,
    default: false,
  },
  borderClass: {
    type: String,
    default: "",
  },
  caretClass: {
    type: String,
    default: "",
  },
  noOptionText: {
    type: String,
    default: "No options available",
  },
  nestedOptions: {
    type: Boolean,
    default: false,
  },
  validator: {
    type: Function,
    default: () => true,
  },
  validationError: {
    type: Function,
    default: () => "Invalid entry",
  },
  testId: {
    type: String,
    default: "",
  },
  nonSelectableOptionTooltip: {
    type: String || null,
    default: null,
  },
  nonSelectableOptionTooltipDirection: {
    type: String,
    default: TOOLTIP_DIRECTION.top,
    validator(value: string) {
      return Object.values(TOOLTIP_DIRECTION).includes(value);
    },
  },
  greyoutParentOptions: {
    type: Boolean,
    default: false,
  },
});

// Generate dropdown ID
const computedDropdownId = computed(() => {
  // If a testId is provided, use it
  if (props.testId) {
    return props.testId;
  }

  // Generate a unique ID for each dropdown instance
  return dropdownId;
});

const optionList = computed(() => {
  const searchText = inputValue.value.trim().toLowerCase();

  if (!props.nestedOptions) {
    return props.options.filter((option) =>
      option.name.toLowerCase().includes(searchText),
    );
  }

  const flattenOptions = (
    options: ListItem[],
    parentId: string | number = "",
  ): ListItem[] => {
    return options.flatMap((option) => {
      const matchesSearch =
        !searchText || option.name.toLowerCase().includes(searchText);
      const newOptionId = parentId ? `${parentId}.${option.id}` : option.id;
      const newSubId = parentId ? `${parentId}.${option.id}` : option.id;

      // Flatten nested options if they exist, using the newOptionId as parent
      const nestedOptions = option.nestedOptions
        ? flattenOptions(option.nestedOptions, newOptionId)
        : [];

      // Include current option only if it matches search criteria
      return matchesSearch
        ? [
            {
              ...option,
              id: Number(newOptionId),
              subId: String(newSubId),
              isNestedOption: !!parentId,
            },
            ...nestedOptions,
          ]
        : nestedOptions;
    });
  };

  return flattenOptions(props.options);
});

const isNestedOptionsParent = (item: ListItem) => {
  return !!item.nestedOptions;
};

const closeDropdown = () => {
  enableInput.value = false;
  errorText.value = ""; // Clear any previous error
};

const toggleDropdown = (event: MouseEvent) => {
  event.stopPropagation();

  // Broadcast event to close other dropdowns
  window.dispatchEvent(
    new CustomEvent("close-360-ui-components-SingleSelects", {
      detail: { excludeId: computedDropdownId },
    }),
  );

  // Toggle current dropdown
  enableInput.value = !enableInput.value;

  // If the dropdown is now open, reset the input value and focus the input box
  if (enableInput.value) {
    nextTick(() => {
      inputValue.value = "";
      inputBox.value?.focus();
    });
  }
};

const handleCloseDropdownEvent = (event: CustomEvent) => {
  // Only close if the event is not excluding this specific dropdown
  if (event.detail.excludeId.value !== computedDropdownId.value) {
    closeDropdown();
  }
};

const handleClickOutside = (event: Event) => {
  event.stopPropagation();
  closeDropdown();
};

const optionSelected = (item: ListItem) => {
  inputValue.value = item.name;
  enableInput.value = false;
  emit("optionSelected", item);
};

const handleAdd = (event: KeyboardEvent) => {
  if (props.allowAdd) {
    if (props.validator(inputValue.value)) {
      // We have valid input
      // Add it and reset the input text
      emit("add-custom", { id: 0, name: inputValue.value });
      inputValue.value = "";
    } else {
      // Invalid input, show error message
      errorText.value = props.validationError(inputValue.value);
    }
  }
  toggleDropdown(event as unknown as MouseEvent);
};

// Add event listener when component is mounted
onMounted(() => {
  window.addEventListener(
    "close-360-ui-components-SingleSelects",
    handleCloseDropdownEvent as EventListener,
  );
});

// Remove event listener when component is unmounted
onUnmounted(() => {
  window.removeEventListener(
    "close-360-ui-components-SingleSelects",
    handleCloseDropdownEvent as EventListener,
  );
});
</script>
